const setTitle = (title) => (title ? `${title} | DeeHr360` : "DeeHr360");

const authRoutes = [
  {
    path: "/auth/sign-in",
    name: "auth.sign-in",
    meta: { title: setTitle("Sign In") },
    component: () => import("@/views/auth/login.vue"),
  },
  {
    // The code step after the password, for anybody who needs Google Authenticator.
    path: "/auth/two-factor",
    name: "auth.two-factor",
    meta: { title: setTitle("Google Authenticator") },
    component: () => import("@/views/auth/two-factor.vue"),
  },
  {
    path: "/auth/reset-pass",
    name: "auth.reset-pass",
    meta: { title: setTitle("Reset Password") },
    component: () => import("@/views/auth/reset-pass.vue"),
  },
  {
    // Shown after asking for a reset link, whether or not the address has an account.
    path: "/auth/forgot-success",
    name: "auth.forgot.success",
    meta: { title: setTitle("Check Your Email") },
    component: () => import("@/views/auth/forgot-pass-success.vue"),
  },
  {
    // Where the emailed reset link lands: /auth/new-password?token=…&email=…
    path: "/auth/new-password",
    name: "auth.new-password",
    meta: { title: setTitle("New Password") },
    component: () => import("@/views/auth/new-password.vue"),
  },
  {
    path: "/set-password/:id",
    name: "auth.set-password",
    meta: { title: setTitle("Set Password") },
    component: () => import("@/views/auth/set-password.vue"),
  },
  {
    path: "/invite/accept",
    name: "auth.invite-accept",
    meta: { title: setTitle("Activate Account") },
    component: () => import("@/views/auth/invite-accept.vue"),
  },
];
const errorRoutes = [
  {
    path: "/auth/error-404",
    name: "error.404",
    meta: { title: setTitle("Error 404") },
    component: () => import("@/views/auth/error-404.vue"),
  },
  {
    path: "/auth/error-500",
    name: "error.500",
    meta: { title: setTitle("Error 500") },
    component: () => import("@/views/auth/error-500.vue"),
  },
  {
    path: "/auth/error-403",
    name: "error.403",
    meta: { title: setTitle("Error 403") },
    component: () => import("@/views/auth/error-403.vue"),
  },
  { path: "/:catchAll(.*)", redirect: "/auth/error-404" },
];

const dashboardRoutes = [
  {
    path: "/",
    name: "dashboards.hr",
    meta: { title: setTitle("Dashboard"), authRequired: true },
    component: () => import("@/views/dashboards/index.vue"),
  },
  {
    path: "/admin/dashboard",
    name: "dashboards.admin",
    meta: {
      title: setTitle("Dashboard"),
      authRequired: true,
      permission: "tenants.view",
    },
    component: () => import("@/views/administration/tenants/dashboard.vue"),
  },
  {
    // The server's log files; they open after a fresh authenticator code.
    path: "/administration/server-logs",
    name: "administration.server-logs",
    meta: {
      title: setTitle("Server Logs"),
      authRequired: true,
      permission: "server-logs.view",
    },
    component: () => import("@/views/administration/server-logs/index.vue"),
  },
  {
    path: "/administration/logs",
    name: "administration.logs",
    meta: {
      title: setTitle("System Logs"),
      authRequired: true,
      permission: "logs.view",
    },
    component: () => import("@/views/administration/logs/index.vue"),
  },
];

// The same screen serves both sides; the API client follows the signed-in scope.
const tenantLogRoutes = [
  {
    path: "/activity-logs",
    name: "logs.activity",
    meta: {
      title: setTitle("Activity Logs"),
      authRequired: true,
      permission: "logs.view",
    },
    component: () => import("@/views/administration/logs/index.vue"),
  },
];

const headcountRoutes = [
  {
    path: "/organisation/headcount-budget",
    name: "headcount.budget",
    meta: {
      title: setTitle("Headcount Budget"),
      authRequired: true,
      permission: "headcount-budgets.view",
    },
    component: () => import("@/views/headcount/budget/index.vue"),
  },
  {
    path: "/organisation/headcount-variance",
    name: "headcount.variance",
    meta: {
      title: setTitle("Headcount Variance"),
      authRequired: true,
      permission: "headcount-budgets.view",
    },
    component: () => import("@/views/headcount/variance/index.vue"),
  },
];

const workforceRoutes = [
  {
    path: "/organisation/workforce",
    name: "workforce.dashboard",
    meta: { title: setTitle("Workforce Dashboard"), authRequired: true, permission: "workforce.view" },
    component: () => import("@/views/workforce/dashboard/index.vue"),
  },
  {
    path: "/organisation/workforce/movements",
    name: "workforce.movements",
    meta: { title: setTitle("Movement Pipeline"), authRequired: true, permission: "workforce.view" },
    component: () => import("@/views/workforce/movements/index.vue"),
  },
  {
    path: "/organisation/workforce/plan",
    name: "workforce.plan",
    meta: { title: setTitle("Workforce Plan"), authRequired: true, permission: "workforce-plans.view" },
    component: () => import("@/views/workforce/plan/index.vue"),
  },
];

const successionRoutes = [
  {
    path: "/organisation/succession",
    name: "succession.board",
    meta: { title: setTitle("Succession Planning"), authRequired: true, permission: "succession.view" },
    component: () => import("@/views/succession/board/index.vue"),
  },
  {
    path: "/organisation/succession/critical-positions",
    name: "succession.critical",
    meta: { title: setTitle("Critical Positions"), authRequired: true, permission: "succession.view" },
    component: () => import("@/views/succession/critical/index.vue"),
  },
  {
    path: "/organisation/succession/talent-pools",
    name: "succession.pools",
    meta: { title: setTitle("Talent Pools"), authRequired: true, permission: "succession.view" },
    component: () => import("@/views/succession/pools/index.vue"),
  },
  {
    path: "/configuration/succession-access",
    name: "succession.access",
    meta: { title: setTitle("Succession Access"), authRequired: true, permission: "succession.configure" },
    component: () => import("@/views/succession/access/index.vue"),
  },
  {
    // Deliberately outside the succession permission: this is what a person may
    // see about their own development, and nothing more.
    path: "/my-development",
    name: "my.development",
    meta: { title: setTitle("My Development"), authRequired: true },
    component: () => import("@/views/succession/my-development/index.vue"),
  },
  {
    // The employee's own cases. No permission: only their own are ever returned.
    path: "/my/disciplinary",
    name: "my.disciplinary",
    meta: { title: setTitle("My Disciplinary Cases"), authRequired: true },
    component: () => import("@/views/disciplinary/my-cases/index.vue"),
  },
  {
    // Complaints about colleagues: raised and followed by the employee, no permission.
    path: "/my/complaints",
    name: "my.complaints",
    meta: { title: setTitle("My Complaints"), authRequired: true },
    component: () => import("@/views/disciplinary/my-complaints/index.vue"),
  },
  {
    path: "/disciplinary/complaints",
    name: "disciplinary.complaints",
    meta: { title: setTitle("Complaints"), authRequired: true, permission: "disciplinary.complaints|disciplinary.manage" },
    component: () => import("@/views/disciplinary/complaints/index.vue"),
  },
  {
    path: "/my/disciplinary/:id",
    name: "my.disciplinary.case",
    meta: { title: setTitle("Disciplinary Case"), authRequired: true },
    component: () => import("@/views/disciplinary/my-cases/detail.vue"),
  },
];

const organogramRoutes = [
  {
    path: "/organisation/structure",
    name: "organogram.units",
    meta: {
      title: setTitle("Organisation Structure"),
      authRequired: true,
      permission: "org-units.view",
    },
    component: () => import("@/views/organogram/units/index.vue"),
  },
  {
    path: "/organisation/structure/:id",
    name: "organogram.unit.detail",
    meta: {
      title: setTitle("Department, Sector or Unit"),
      authRequired: true,
      permission: "org-units.view",
    },
    component: () => import("@/views/organogram/unit-detail/index.vue"),
  },
  {
    path: "/organisation/levels",
    name: "organogram.levels",
    meta: {
      title: setTitle("Organisation Levels"),
      authRequired: true,
      permission: "org-unit-types.view",
    },
    component: () => import("@/views/organogram/levels/index.vue"),
  },
  {
    path: "/organisation/job-titles",
    name: "organogram.job-titles",
    meta: {
      title: setTitle("Job Titles"),
      authRequired: true,
      permission: "job-titles.view",
    },
    component: () => import("@/views/organogram/job-titles/index.vue"),
  },
  {
    path: "/organisation/positions",
    name: "organogram.positions",
    meta: {
      title: setTitle("Positions"),
      authRequired: true,
      permission: "positions.view",
    },
    component: () => import("@/views/organogram/positions/index.vue"),
  },
  {
    path: "/organisation/job-grades",
    name: "organogram.job-grades",
    meta: {
      title: setTitle("Job Grades"),
      authRequired: true,
      permission: "job-grades.view",
    },
    component: () => import("@/views/organogram/job-grades/index.vue"),
  },
  {
    path: "/organisation/job-bands",
    name: "organogram.job-bands",
    meta: {
      title: setTitle("Job Bands"),
      authRequired: true,
      permission: "job-bands.view",
    },
    component: () => import("@/views/organogram/job-bands/index.vue"),
  },
  {
    path: "/organisation/chart",
    name: "organogram.chart",
    meta: { title: setTitle("Org Chart"), authRequired: true, permission: "org-units.view" },
    component: () => import("@/views/organogram/chart/index.vue"),
  },
];

const exportRoutes = [
  {
    path: "/exports",
    name: "exports.list",
    meta: { title: setTitle("My Exports"), authRequired: true, permission: "exports.view" },
    component: () => import("@/views/exports/index.vue"),
  },
  {
    // The link in the "your export is ready" email lands here.
    path: "/exports/:id",
    name: "exports.show",
    meta: { title: setTitle("My Exports"), authRequired: true, permission: "exports.view" },
    component: () => import("@/views/exports/index.vue"),
  },
  {
    // Imports run in the background; each person sees only their own. No
    // permission of its own: each import type is guarded where it is started.
    path: "/imports",
    name: "imports.list",
    meta: { title: setTitle("My Imports"), authRequired: true },
    component: () => import("@/views/imports/index.vue"),
  },
  {
    // The link in the "your file has been checked / imported" email lands here.
    path: "/imports/:id",
    name: "imports.show",
    meta: { title: setTitle("My Imports"), authRequired: true },
    component: () => import("@/views/imports/index.vue"),
  },
];

const companyRoutes = [
  {
    path: "/company",
    name: "company.profile",
    meta: {
      title: setTitle("Company Profile"),
      authRequired: true,
      permission: "company.view",
    },
    component: () => import("@/views/company/index.vue"),
  },
  {
    path: "/company/moving-over",
    name: "company.moving-over",
    meta: {
      title: setTitle("Moving from another system"),
      authRequired: true,
      permission: "org-units.create|employees.create|employees.update|salary.manage|leave-types.create|leave-allocations.manage|payroll.create",
    },
    component: () => import("@/views/company/moving-over.vue"),
  },
];

const administrationRoutes = [
  {
    path: "/administration/admin-roles",
    name: "administration.admin-roles",
    meta: { title: setTitle("Roles and rights"), authRequired: true, permission: "admin-roles.view" },
    component: () => import("@/views/administration/admin-roles/index.vue"),
  },
  {
    path: "/administration/client-usage",
    name: "administration.client-usage",
    meta: { title: setTitle("Client Usage"), authRequired: true, permission: "client-usage.view" },
    component: () => import("@/views/administration/client-usage/index.vue"),
  },
  {
    path: "/administration/client-usage/:id",
    name: "administration.client-usage.company",
    meta: { title: setTitle("Company Usage"), authRequired: true, permission: "client-usage.view" },
    component: () => import("@/views/administration/client-usage/company.vue"),
  },
  {
    path: "/administration/website-traffic",
    name: "administration.website-traffic",
    meta: { title: setTitle("Website Traffic"), authRequired: true, permission: "website-traffic.view" },
    component: () => import("@/views/administration/website-traffic/index.vue"),
  },
  {
    path: "/administration/trial-applications",
    name: "administration.trial-applications",
    meta: { title: setTitle("Trial Applications"), authRequired: true, permission: "trial-applications.view" },
    component: () => import("@/views/administration/trial-applications/index.vue"),
  },
  {
    path: "/administration/website",
    name: "administration.website",
    meta: { title: setTitle("Website Settings"), authRequired: true, permission: "website.view" },
    component: () => import("@/views/administration/website/index.vue"),
  },
  {
    path: "/administration/statutory-rates",
    name: "administration.statutory",
    meta: { title: setTitle("Standard Statutory Rates"), authRequired: true, permission: "statutory.view" },
    component: () => import("@/views/administration/statutory/index.vue"),
  },
  {
    path: "/administration/integrations",
    name: "administration.integrations",
    meta: { title: setTitle("Integrations"), authRequired: true, permission: "platform-integrations.view" },
    component: () => import("@/views/administration/integrations/index.vue"),
  },
  {
    path: "/administration/api-docs-access",
    name: "administration.api-docs-access",
    meta: { title: setTitle("API Documentation Access"), authRequired: true, permission: "api-docs.view" },
    component: () => import("@/views/administration/api-docs-access/index.vue"),
  },
  {
    path: "/administration/billing/plans",
    name: "administration.billing.plans",
    meta: { title: setTitle("Plans"), authRequired: true, permission: "plans.view" },
    component: () => import("@/views/administration/billing/plans.vue"),
  },
  {
    path: "/administration/billing/methods",
    name: "administration.billing.methods",
    meta: { title: setTitle("Payment Methods"), authRequired: true, permission: "billing.view" },
    component: () => import("@/views/administration/billing/methods.vue"),
  },
  {
    path: "/administration/billing/schedules",
    name: "administration.billing.schedules",
    meta: { title: setTitle("Payment Schedules"), authRequired: true, permission: "billing.view" },
    component: () => import("@/views/administration/billing/schedules.vue"),
  },
  {
    path: "/administration/catalogue",
    name: "administration.catalogue",
    meta: { title: setTitle("Modules Catalogue"), authRequired: true, permission: "catalogue.view" },
    component: () => import("@/views/administration/catalogue/index.vue"),
  },
  {
    path: "/administration/tenants",
    name: "administration.tenants.list",
    meta: {
      title: setTitle("Manage Tenants"),
      authRequired: true,
      permission: "tenants.view",
    },
    component: () => import("@/views/administration/tenants/index.vue"),
  },
  {
    path: "/administration/tenants/create",
    name: "administration.tenants.create",
    meta: {
      title: setTitle("Create Tenant"),
      authRequired: true,
      permission: "tenants.create",
    },
    component: () => import("@/views/administration/tenants/create.vue"),
  },
  {
    path: "/administration/admins",
    name: "administration.admins.list",
    meta: {
      title: setTitle("Admin Users"),
      authRequired: true,
      permission: "admins.view",
    },
    component: () => import("@/views/administration/admins/index.vue"),
  },
];

const employeeRoutes = [
  {
    path: "/employees",
    name: "employees.list",
    meta: { title: setTitle("Employees"), authRequired: true, permission: "employees.view" },
    component: () => import("@/views/employees/list/index.vue"),
  },
  {
    path: "/employees/create",
    name: "employees.create",
    meta: { title: setTitle("Register an Employee"), authRequired: true, permission: "employees.create" },
    // The guided registration. The single form remains, for editing a record
    // that already exists — a wizard is the wrong shape for changing one field.
    component: () => import("@/views/employees/register/index.vue"),
  },
  ...[
    { slug: "verification", name: "employees.verification", title: "Verification", permission: "employees.view" },
    { slug: "approval", name: "employees.approval", title: "Approval", permission: "employees.view" },
    { slug: "access", name: "employees.access", title: "System Access", permission: "employees.view" },
    { slug: "active", name: "employees.active", title: "Active System Users", permission: "employees.view" },
    { slug: "rejected", name: "employees.rejected", title: "Rejected", permission: "employees.view" },
  ].map((stage) => ({
    // One screen serves every stage; `meta.stage` is what it reads.
    path: `/employees/pipeline/${stage.slug}`,
    name: stage.name,
    meta: {
      title: setTitle(stage.title),
      authRequired: true,
      permission: stage.permission,
      stage: stage.slug,
    },
    component: () => import("@/views/employees/pipeline/index.vue"),
  })),
  {
    path: "/employees/:id/edit",
    name: "employees.edit",
    meta: { title: setTitle("Edit Employee"), authRequired: true, permission: "employees.update" },
    component: () => import("@/views/employees/form/index.vue"),
  },
  {
    path: "/employees/:id",
    name: "employees.profile",
    meta: { title: setTitle("Employee Profile"), authRequired: true, permission: "employees.view" },
    component: () => import("@/views/employees/profile/index.vue"),
  },
  {
    path: "/employees/offboarding",
    name: "employees.offboarding",
    meta: { title: setTitle("Exits"), authRequired: true, permission: "offboarding.view" },
    component: () => import("@/views/employees/offboarding/index.vue"),
  },
  {
    path: "/employees/offboarding-queue",
    name: "employees.offboarding-queue",
    meta: { title: setTitle("Clearance"), authRequired: true, permission: "offboarding.clear" },
    component: () => import("@/views/employees/offboarding-queue/index.vue"),
  },
  {
    path: "/employees/offboarding-templates",
    name: "employees.offboarding-templates",
    meta: { title: setTitle("Exit Checklists"), authRequired: true, permission: "offboarding.view" },
    component: () => import("@/views/employees/offboarding-templates/index.vue"),
  },
];

const onboardingRoutes = [
  {
    path: "/onboarding",
    name: "onboarding.list",
    meta: { title: setTitle("Onboarding"), authRequired: true, permission: "onboarding.view" },
    component: () => import("@/views/onboarding/progress/index.vue"),
  },
  {
    path: "/onboarding/templates",
    name: "onboarding.templates",
    meta: { title: setTitle("Onboarding Templates"), authRequired: true, permission: "onboarding-templates.view" },
    component: () => import("@/views/onboarding/templates/index.vue"),
  },
  {
    path: "/probation",
    name: "probation.list",
    meta: { title: setTitle("Probation"), authRequired: true, permission: "probation.view" },
    component: () => import("@/views/onboarding/probation/index.vue"),
  },
  {
    path: "/policies",
    name: "policies.list",
    // Everybody reads and accepts policies here; the acceptance figures on it
    // are shown only to those who may see them.
    meta: { title: setTitle("Policies"), authRequired: true, permission: "policies.view|policies.read" },
    component: () => import("@/views/onboarding/policies/index.vue"),
  },
  {
    path: "/access-requests",
    name: "access-requests.list",
    meta: { title: setTitle("System Access"), authRequired: true, permission: "access-requests.view" },
    component: () => import("@/views/onboarding/access-requests/index.vue"),
  },
  {
    // What access can be requested for — the company's own list of systems.
    path: "/configuration/systems",
    name: "onboarding.systems",
    meta: { title: setTitle("Systems"), authRequired: true, permission: "systems.view" },
    component: () => import("@/views/onboarding/systems/index.vue"),
  },
];

// Still scaffolding — kept reachable rather than removed.
const employeeScaffoldingRoutes = [
  {
    path: "/employees/policy-acknowledgements",
    name: "employees.policy-acknowledgements",
    meta: { title: setTitle("Policy Acknowledgements"), authRequired: true, permission: "policies.view" },
    component: () => import("@/views/employees/policy-acknowledgements/index.vue"),
  },
  {
    path: "/employees/transfers",
    name: "employees.transfers",
    meta: { title: setTitle("Transfers & Promotions"), authRequired: true, permission: "employees.transfer|employees.promote|employees.view-all" },
    component: () => import("@/views/employees/transfers/index.vue"),
  },
];

const configRoutes = [
  {
    path: "/configuration/approval-matrix",
    name: "config.approval-matrix",
    meta: { title: setTitle("Approval Matrix"), authRequired: true, permission: "approval-workflows.view" },
    component: () => import("@/views/config/approval-matrix/index.vue"),
  },
  {
    path: "/configuration/letter-templates",
    name: "config.letter-templates",
    meta: { title: setTitle("Letter Templates"), authRequired: true, permission: "templates.view|templates.manage|letters.generate" },
    component: () => import("@/views/config/letter-templates/index.vue"),
  },
  {
    path: "/configuration/letter-templates/:id",
    name: "config.letter-template",
    meta: { title: setTitle("Letter Template"), authRequired: true, permission: "templates.view|templates.manage" },
    component: () => import("@/views/config/letter-templates/editor.vue"),
  },
  {
    path: "/configuration/sensitive-fields",
    name: "config.sensitive-fields",
    meta: { title: setTitle("Sensitive Fields"), authRequired: true, permission: "sensitive-fields.manage" },
    component: () => import("@/views/config/sensitive-fields/index.vue"),
  },
];

const approvalRoutes = [
  {
    path: "/approvals",
    name: "approvals.list",
    meta: { title: setTitle("Approvals"), authRequired: true, permission: "approvals.view" },
    component: () => import("@/views/approvals/index.vue"),
  },
];

const documentRoutes = [
  {
    path: "/documents/browse",
    name: "documents.browse",
    meta: { title: setTitle("Browse Documents"), authRequired: true, permission: "documents.view" },
    component: () => import("@/views/documents/browse/index.vue"),
  },
  {
    path: "/documents/browse/:folderId",
    name: "documents.browse.folder",
    meta: { title: setTitle("Browse Documents"), authRequired: true, permission: "documents.view" },
    component: () => import("@/views/documents/browse/index.vue"),
  },
  {
    path: "/documents/categories",
    name: "documents.categories",
    meta: { title: setTitle("Document Categories"), authRequired: true, permission: "documents.view" },
    component: () => import("@/views/documents/categories/index.vue"),
  },
  {
    path: "/documents/all",
    name: "documents.all",
    meta: { title: setTitle("All Documents"), authRequired: true, permission: "documents.view" },
    component: () => import("@/views/documents/all-documents/index.vue"),
  },
];
const recruitmentRoutes = [
  {
    path: "/recruitment/pipelines",
    name: "recruitment.pipelines",
    meta: { title: setTitle("Hiring Sequences"), authRequired: true, permission: "recruitment-pipelines.view" },
    component: () => import("@/views/recruitment/pipelines/index.vue"),
  },
  {
    path: "/recruitment/requisitions",
    name: "recruitment.requisitions",
    meta: { title: setTitle("Job Requisitions"), authRequired: true, permission: "requisitions.view" },
    component: () => import("@/views/recruitment/requisitions/index.vue"),
  },
  {
    path: "/recruitment/postings",
    name: "recruitment.postings",
    meta: { title: setTitle("Job Postings"), authRequired: true, permission: "postings.view" },
    component: () => import("@/views/recruitment/postings/index.vue"),
  },
  {
    path: "/recruitment/postings/:id",
    name: "recruitment.posting.detail",
    meta: { title: setTitle("Advert Results"), authRequired: true, permission: "postings.view" },
    component: () => import("@/views/recruitment/postings-detail/index.vue"),
  },
  {
    path: "/recruitment/applications",
    name: "recruitment.applications",
    meta: { title: setTitle("Applications"), authRequired: true, permission: "applications.view" },
    component: () => import("@/views/recruitment/applications/index.vue"),
  },
  {
    path: "/recruitment/external-panellists",
    name: "recruitment.external-panellists",
    meta: { title: setTitle("External Panellists"), authRequired: true, permission: "applications.view" },
    component: () => import("@/views/recruitment/external-panellists/index.vue"),
  },
  {
    path: "/recruitment/interviews",
    name: "recruitment.interviews",
    meta: { title: setTitle("Interviews"), authRequired: true, permission: "applications.view" },
    component: () => import("@/views/recruitment/interviews/index.vue"),
  },
  {
    path: "/recruitment/interviews/schedule",
    name: "recruitment.interview.schedule",
    meta: { title: setTitle("Arrange an Interview"), authRequired: true, permission: "applications.manage" },
    component: () => import("@/views/recruitment/interview-schedule/index.vue"),
  },
  {
    path: "/recruitment/interviews/calendar",
    name: "recruitment.interview.calendar",
    meta: { title: setTitle("Interview Calendar"), authRequired: true, permission: "applications.view" },
    component: () => import("@/views/recruitment/interview-calendar/index.vue"),
  },
];
const attendanceRoutes = [
  // Setup
  {
    // Tasks, My Day and timesheets: everybody's own, so no permission on the
    // routes; what else somebody sees is decided by the API.
    path: "/my-day",
    name: "tasks.my-day",
    meta: { title: setTitle("My Day"), authRequired: true },
    component: () => import("@/views/tasks/my-day/index.vue"),
  },
  {
    path: "/tasks",
    name: "tasks.index",
    meta: { title: setTitle("Tasks"), authRequired: true },
    component: () => import("@/views/tasks/index.vue"),
  },
  {
    path: "/tasks/:id",
    name: "tasks.show",
    meta: { title: setTitle("Task"), authRequired: true },
    component: () => import("@/views/tasks/detail/index.vue"),
  },
  {
    path: "/turnaround",
    name: "tasks.turnaround",
    meta: { title: setTitle("Turnaround & SLA"), authRequired: true },
    component: () => import("@/views/tasks/turnaround/index.vue"),
  },
  {
    path: "/reminders",
    name: "reminders.index",
    meta: { title: setTitle("Reminders"), authRequired: true },
    component: () => import("@/views/reminders/index.vue"),
  },
  {
    path: "/timesheets",
    name: "tasks.timesheets",
    meta: { title: setTitle("Timesheets"), authRequired: true },
    component: () => import("@/views/tasks/timesheets/index.vue"),
  },
  {
    // Everybody's own goals, and their team's; what each person sees is
    // decided by the API, so there is no permission on the route itself.
    path: "/goals",
    name: "goals.index",
    meta: { title: setTitle("Goals"), authRequired: true },
    component: () => import("@/views/goals/index.vue"),
  },
  {
    // Goal and task analytics: own, team, department or organisation, as the API allows.
    path: "/goals/analytics",
    name: "goals.analytics",
    meta: { title: setTitle("Goal analytics"), authRequired: true },
    component: () => import("@/views/goals/analytics/index.vue"),
  },
  {
    // Closing goals out: what to review, what waits for sign-off, what is scored.
    path: "/goals/reviews",
    name: "goals.reviews",
    meta: { title: setTitle("Goal reviews"), authRequired: true },
    component: () => import("@/views/goals/reviews/index.vue"),
  },
  {
    path: "/goals/:id",
    name: "goals.show",
    meta: { title: setTitle("Goal"), authRequired: true },
    component: () => import("@/views/goals/detail/index.vue"),
  },
  {
    // How goals are set up: rules, measuring and scoring, categories and the
    // performance calendar. Everything else in Goals & Tasks sits inside it.
    path: "/goals/configuration",
    name: "goals.configuration",
    meta: { title: setTitle("Goals & Tasks Configuration"), authRequired: true, permission: "goals-config.view" },
    component: () => import("@/views/goals/configuration/index.vue"),
  },
  {
    path: "/attendance/shifts",
    name: "attendance.shifts",
    meta: { title: setTitle("Shifts"), authRequired: true, permission: "work-schedules.view" },
    component: () => import("@/views/attendance/shifts/index.vue"),
  },
  {
    path: "/attendance/rules",
    name: "attendance.rules",
    meta: { title: setTitle("Attendance Rules"), authRequired: true, permission: "attendance-rules.view" },
    component: () => import("@/views/attendance/attendance-rules/index.vue"),
  },
  {
    path: "/attendance/holidays",
    name: "attendance.holidays",
    // The calendar the register reads. It belongs to leave's configuration —
    // leave does not count a holiday against anybody's balance either — so it
    // carries that right rather than one of attendance's own.
    meta: { title: setTitle("Public Holidays"), authRequired: true, permission: "leave-config.view" },
    component: () => import("@/views/attendance/public-holidays/index.vue"),
  },
  {
    path: "/attendance/work-schedules",
    name: "attendance.work-schedules",
    meta: { title: setTitle("Work Schedules"), authRequired: true, permission: "work-schedules.view" },
    component: () => import("@/views/attendance/work-schedules/index.vue"),
  },
  // Daily Operations
  {
    // Everybody's own attendance. Needs no permission — the API takes the
    // employee from the login, as clocking and payslips do.
    path: "/attendance/mine",
    name: "attendance.mine",
    meta: { title: setTitle("My Attendance"), authRequired: true },
    component: () => import("@/views/attendance/my-attendance/index.vue"),
  },
  {
    path: "/attendance/clock-in-out",
    name: "attendance.clock-in-out",
    meta: { title: setTitle("Clock In / Out"), authRequired: true },
    component: () => import("@/views/attendance/clock-in-out/index.vue"),
  },
  {
    path: "/attendance/daily",
    name: "attendance.daily",
    meta: { title: setTitle("Daily Attendance"), authRequired: true, permission: "attendance.view" },
    component: () => import("@/views/attendance/daily-attendance/index.vue"),
  },
  {
    // One unit's attendance, for whoever runs it. No permission of its own:
    // somebody without the right to see others is narrowed to their own unit.
    path: "/attendance/department",
    name: "attendance.department",
    meta: { title: setTitle("Department Attendance"), authRequired: true },
    component: () => import("@/views/attendance/department/index.vue"),
  },
  {
    // Everything in the register that needs a person to look at it.
    path: "/attendance/exceptions",
    name: "attendance.exceptions",
    meta: { title: setTitle("Exceptions"), authRequired: true, permission: "attendance.view" },
    component: () => import("@/views/attendance/exceptions/index.vue"),
  },
  {
    // The places, networks and devices the clocking checks are made against.
    path: "/attendance/clocking-rules",
    name: "attendance.clocking-rules",
    meta: { title: setTitle("Clocking Rules"), authRequired: true, permission: "attendance-rules.view" },
    component: () => import("@/views/attendance/clocking-rules/index.vue"),
  },
  {
    // Freezing a month so payroll can be built on figures that stop moving.
    path: "/attendance/periods",
    name: "attendance.periods",
    meta: { title: setTitle("Closing the Month"), authRequired: true, permission: "attendance-periods.view" },
    component: () => import("@/views/attendance/periods/index.vue"),
  },
  {
    path: "/attendance/corrections",
    name: "attendance.corrections",
    meta: { title: setTitle("Attendance Corrections"), authRequired: true },
    component: () => import("@/views/attendance/corrections/index.vue"),
  },
  // Shifts
  {
    path: "/attendance/shift-assignments",
    name: "attendance.shift-assignments",
    meta: { title: setTitle("Shift Assignments"), authRequired: true, permission: "work-schedules.view" },
    component: () => import("@/views/attendance/shift-assignments/index.vue"),
  },
  {
    path: "/attendance/rotational",
    name: "attendance.rotational",
    meta: { title: setTitle("Rotations"), authRequired: true, permission: "work-schedules.view" },
    component: () =>
      import("@/views/attendance/rotational-schedules/index.vue"),
  },
  // OT
  {
    path: "/attendance/ot-types",
    name: "attendance.ot-types",
    meta: { title: setTitle("OT Types & Rules"), authRequired: true, permission: "attendance-rules.view" },
    component: () => import("@/views/attendance/ot-types/index.vue"),
  },
  {
    path: "/attendance/ot-requests",
    name: "attendance.ot-requests",
    meta: { title: setTitle("OT Requests"), authRequired: true },
    component: () => import("@/views/attendance/ot-requests/index.vue"),
  },
  // Working away
  {
    path: "/attendance/wfh",
    name: "attendance.wfh",
    meta: { title: setTitle("WFH Requests"), authRequired: true },
    component: () => import("@/views/attendance/wfh-requests/index.vue"),
  },
  // Reports
  {
    path: "/attendance/reports/register",
    name: "attendance.reports.register",
    meta: { title: setTitle("Attendance Register"), authRequired: true, permission: "attendance.view" },
    component: () =>
      import("@/views/attendance/reports/attendance-register/index.vue"),
  },
  {
    path: "/attendance/reports/late",
    name: "attendance.reports.late",
    meta: { title: setTitle("Late Coming Report"), authRequired: true, permission: "attendance.view" },
    component: () => import("@/views/attendance/reports/late-coming/index.vue"),
  },
  {
    path: "/attendance/reports/overtime",
    name: "attendance.reports.overtime",
    meta: { title: setTitle("Overtime Report"), authRequired: true, permission: "attendance.view" },
    component: () =>
      import("@/views/attendance/reports/overtime-report/index.vue"),
  },
  {
    path: "/attendance/reports/absence",
    name: "attendance.reports.absence",
    meta: { title: setTitle("Absence Report"), authRequired: true, permission: "attendance.view" },
    component: () =>
      import("@/views/attendance/reports/absence-report/index.vue"),
  },
  {
    path: "/attendance/reports/monthly",
    name: "attendance.reports.monthly",
    meta: { title: setTitle("Monthly Summary"), authRequired: true, permission: "attendance.view" },
    component: () =>
      import("@/views/attendance/reports/monthly-summary/index.vue"),
  },
];
const systemAdministrationRoutes = [
  {
    path: "/system-administration/roles",
    name: "system-administration.roles",
    meta: { title: setTitle("Roles"), authRequired: true, permission: "roles.view" },
    component: () => import("@/views/system-administration/roles/index.vue"),
  },
  {
    path: "/system-administration/role-permissions",
    name: "system-administration.role-permissions",
    meta: { title: setTitle("Role Permissions"), authRequired: true, permission: "role-permissions.view" },
    component: () => import("@/views/system-administration/role-permissions/index.vue"),
  },
  {
    path: "/system-administration/role-assignments",
    name: "system-administration.role-assignments",
    meta: { title: setTitle("Role Assignment"), authRequired: true, permission: "role-assignments.view" },
    component: () => import("@/views/system-administration/role-assignments/index.vue"),
  },
  {
    path: "/system-administration/integrations",
    name: "system-administration.integrations",
    meta: { title: setTitle("Integrations"), authRequired: true, permission: "integrations.view" },
    component: () => import("@/views/system-administration/integrations/index.vue"),
  },
  {
    path: "/system-administration/integrations/:id",
    name: "system-administration.integration",
    meta: { title: setTitle("Integration App"), authRequired: true, permission: "integrations.view" },
    component: () => import("@/views/system-administration/integration-detail/index.vue"),
  },
];

const leaveRoutes = [
  {
    // The colleague confirming a handover arrives here straight from their
    // email, so this one is deliberately outside the signed-in area.
    path: "/handover/:token",
    name: "leave.handover",
    meta: { title: setTitle("Confirm a handover"), authRequired: false },
    component: () => import("@/views/leave/handover/index.vue"),
  },
  {
    path: "/leave/types",
    name: "leave.types",
    meta: { title: setTitle("Leave Types"), authRequired: true, permission: "leave-types.view" },
    component: () => import("@/views/leave/types/index.vue"),
  },
  {
    path: "/leave/policies",
    name: "leave.policies",
    meta: { title: setTitle("Leave Policies"), authRequired: true, permission: "leave-policies.view" },
    component: () => import("@/views/leave/policies/index.vue"),
  },
  {
    path: "/leave/allocations",
    name: "leave.allocations",
    meta: { title: setTitle("Leave Balances"), authRequired: true, permission: "leave-allocations.view" },
    component: () => import("@/views/leave/allocations/index.vue"),
  },
  {
    path: "/leave/applications",
    name: "leave.applications",
    meta: { title: setTitle("Applications"), authRequired: true, permission: "leave.view" },
    component: () => import("@/views/leave/applications/index.vue"),
  },
  {
    path: "/leave/approvals",
    name: "leave.approvals",
    meta: { title: setTitle("Leave Approvals"), authRequired: true, permission: "leave.view&approvals.act" },
    component: () => import("@/views/leave/approvals/index.vue"),
  },
  {
    // Work colleagues have asked the signed-in employee to cover.
    path: "/leave/handovers",
    name: "leave.my-handovers",
    meta: { title: setTitle("My Handovers"), authRequired: true, permission: "leave.view" },
    component: () => import("@/views/leave/my-handovers/index.vue"),
  },
  {
    path: "/leave/calendar",
    name: "leave.calendar",
    meta: { title: setTitle("Leave Calendar"), authRequired: true, permission: "leave.view" },
    component: () => import("@/views/leave/calendar/index.vue"),
  },
  {
    path: "/leave/configuration",
    name: "leave.configuration",
    meta: { title: setTitle("Leave Configuration"), authRequired: true, permission: "leave-config.view" },
    component: () => import("@/views/leave/configuration/index.vue"),
  },
  {
    path: "/leave/balance-report",
    name: "leave.balance-report",
    meta: { title: setTitle("Balance Report"), authRequired: true, permission: "leave-allocations.view" },
    component: () => import("@/views/leave/balance-report/index.vue"),
  },
];
const payrollRoutes = [
  {
    path: "/payroll/advances",
    name: "payroll.advances",
    meta: { title: setTitle("Salary Advances"), authRequired: true, permission: "advances.view" },
    component: () => import("@/views/payroll/advances/index.vue"),
  },
  {
    path: "/payroll/advance-rules",
    name: "payroll.advance-rules",
    meta: { title: setTitle("Advance Rules"), authRequired: true, permission: "advance-rules.manage" },
    component: () => import("@/views/payroll/advance-rules/index.vue"),
  },
  {
    // Everybody's own payslips. Needs no payroll right — the API checks that
    // each record belongs to whoever is asking.
    path: "/my/payslips",
    name: "my.payslips",
    meta: { title: setTitle("My Payslips"), authRequired: true },
    component: () => import("@/views/self-service/payslips/index.vue"),
  },
  {
    // Everybody's own. Separate from the register above, which is HR's.
    path: "/my/advances",
    name: "my.advances",
    meta: { title: setTitle("My Advances"), authRequired: true, permission: "advances.apply" },
    component: () => import("@/views/self-service/advances/index.vue"),
  },
  {
    path: "/my/assets",
    name: "my.assets",
    meta: { title: setTitle("My Assets"), authRequired: true },
    component: () => import("@/views/self-service/assets/index.vue"),
  },
  {
    path: "/payroll/settings",
    name: "payroll.settings",
    meta: { title: setTitle("Payroll Settings"), authRequired: true, permission: "payroll-config.view" },
    component: () => import("@/views/payroll/settings/index.vue"),
  },
  {
    path: "/payroll/statutory-rules",
    name: "payroll.statutory-rules",
    meta: { title: setTitle("Statutory Rules"), authRequired: true, permission: "payroll-rules.view" },
    component: () => import("@/views/payroll/statutory-rules/index.vue"),
  },
  {
    path: "/payroll/calculator",
    name: "payroll.calculator",
    meta: { title: setTitle("Payslip Calculator"), authRequired: true, permission: "payroll.view" },
    component: () => import("@/views/payroll/calculator/index.vue"),
  },
  {
    path: "/payroll/payslip-preview",
    name: "payroll.payslip-preview",
    meta: { title: setTitle("Payslip Preview"), authRequired: true, permission: "payroll.view" },
    component: () => import("@/views/payroll/payslip-preview/index.vue"),
  },
  {
    path: "/payroll/groups",
    name: "payroll.groups",
    meta: { title: setTitle("Payroll Groups"), authRequired: true, permission: "payroll-config.view" },
    component: () => import("@/views/payroll/groups/index.vue"),
  },
  {
    path: "/payroll/allowance-types",
    name: "payroll.allowance-types",
    meta: { title: setTitle("Allowance Types"), authRequired: true, permission: "payroll-config.view" },
    component: () => import("@/views/payroll/allowance-types/index.vue"),
  },
  {
    path: "/payroll/deduction-types",
    name: "payroll.deduction-types",
    meta: { title: setTitle("Deduction Types"), authRequired: true, permission: "payroll-config.view" },
    component: () => import("@/views/payroll/deduction-types/index.vue"),
  },
  {
    path: "/payroll/bonus-types",
    name: "payroll.bonus-types",
    meta: { title: setTitle("Bonus Types"), authRequired: true, permission: "payroll-config.view" },
    component: () => import("@/views/payroll/bonus-types/index.vue"),
  },
  {
    path: "/payroll/salary-adjustments",
    name: "payroll.salary-adjustments",
    meta: { title: setTitle("Salary Adjustments"), authRequired: true, permission: "salary.view" },
    component: () => import("@/views/payroll/salary-adjustments/index.vue"),
  },
  {
    path: "/payroll/employee-salary",
    name: "payroll.employee-salary",
    meta: { title: setTitle("Salary & Allowances"), authRequired: true, permission: "salary.view" },
    component: () => import("@/views/payroll/employee-salary/index.vue"),
  },
  {
    path: "/payroll/period-entries",
    name: "payroll.period-entries",
    meta: { title: setTitle("Period Entries"), authRequired: true, permission: "salary.view" },
    component: () => import("@/views/payroll/period-entries/index.vue"),
  },
  {
    path: "/payroll/employee-deductions",
    name: "payroll.employee-deductions",
    meta: { title: setTitle("Employee Deductions"), authRequired: true, permission: "salary.view" },
    component: () => import("@/views/payroll/employee-deductions/index.vue"),
  },
  {
    path: "/payroll/variance",
    name: "payroll.variance",
    meta: { title: setTitle("Variance Analysis"), authRequired: true, permission: "salary.view" },
    component: () => import("@/views/payroll/variance/index.vue"),
  },
  {
    path: "/payroll/runs",
    name: "payroll.runs",
    meta: { title: setTitle("Payroll Runs"), authRequired: true, permission: "payroll.view" },
    component: () => import("@/views/payroll/runs/index.vue"),
  },
  {
    path: "/payroll/runs/:id",
    name: "payroll.run.detail",
    meta: { title: setTitle("Run Detail"), authRequired: true, permission: "payroll.view" },
    component: () => import("@/views/payroll/run-detail/index.vue"),
  },
  {
    path: "/payroll/payslips",
    name: "payroll.payslips",
    meta: { title: setTitle("Payslips"), authRequired: true },
    component: () => import("@/views/payroll/payslips/index.vue"),
  },
  {
    path: "/payroll/bank-formats",
    name: "payroll.bank-formats",
    meta: { title: setTitle("Bank Formats"), authRequired: true, permission: "payroll-config.view" },
    component: () => import("@/views/payroll/bank-formats/index.vue"),
  },
  {
    path: "/payroll/bank-file",
    name: "payroll.bank-file",
    meta: { title: setTitle("Bank Payment File"), authRequired: true, permission: "payroll.view" },
    component: () => import("@/views/payroll/bank-file/index.vue"),
  },
  {
    path: "/payroll/statutory-returns",
    name: "payroll.statutory-returns",
    meta: { title: setTitle("Statutory Returns"), authRequired: true, permission: "payroll.view" },
    component: () => import("@/views/payroll/statutory-returns/index.vue"),
  },
  {
    path: "/payroll/remittances",
    name: "payroll.remittances",
    meta: { title: setTitle("Remittance Breakdown"), authRequired: true, permission: "payroll.view" },
    component: () => import("@/views/payroll/remittances/index.vue"),
  },
  {
    /*
     * The old P9 path, kept so a bookmark still lands somewhere sensible.
     * P9 is now one tab of the returns screen rather than a screen of its own,
     * because nobody produces a P9 without also needing the schedules.
     */
    path: "/payroll/p9-report",
    redirect: { name: "payroll.statutory-returns" },
  },
];
const performanceRoutes = [
  {
    path: "/performance/cycles",
    name: "performance.cycles",
    meta: { title: setTitle("Appraisal Rounds"), authRequired: true, permission: "performance-cycles.view" },
    component: () => import("@/views/performance/cycles/index.vue"),
  },
  {
    path: "/performance/cycles/:id",
    name: "performance.cycle",
    meta: { title: setTitle("Appraisal Round"), authRequired: true, permission: "performance-cycles.view" },
    component: () => import("@/views/performance/cycles/detail.vue"),
  },
  {
    // Scales, criteria, forms and how appraisals run — company-wide and per unit.
    path: "/performance/setup",
    name: "performance.setup",
    meta: { title: setTitle("Appraisal Setup"), authRequired: true, permission: "performance-config.view" },
    component: () => import("@/views/performance/setup/index.vue"),
  },
  {
    path: "/performance/setup/forms/:id",
    name: "performance.setup.form",
    meta: { title: setTitle("Appraisal Form"), authRequired: true, permission: "performance-config.view" },
    component: () => import("@/views/performance/setup/form.vue"),
  },
  {
    path: "/performance/kpis",
    name: "performance.kpis",
    meta: { title: setTitle("KPIs"), authRequired: true, permission: "goals.create-own|goals.assign|goals.view-all|appraisals.view-all" },
    component: () => import("@/views/performance/kpis/index.vue"),
  },
  {
    path: "/performance/calibration",
    name: "performance.calibration",
    meta: { title: setTitle("Calibration"), authRequired: true, permission: "performance-cycles.view" },
    component: () => import("@/views/performance/calibration/index.vue"),
  },
  {
    path: "/performance/appraisals",
    name: "performance.appraisals",
    meta: { title: setTitle("Appraisals"), authRequired: true },
    component: () => import("@/views/performance/appraisals/index.vue"),
  },
  {
    path: "/performance/appraisals/:id",
    name: "performance.appraisal",
    meta: { title: setTitle("Appraisal"), authRequired: true },
    component: () => import("@/views/performance/appraisals/detail.vue"),
  },
  {
    path: "/performance/reports",
    name: "performance.reports",
    meta: { title: setTitle("Performance Reports"), authRequired: true, permission: "performance-cycles.view" },
    component: () => import("@/views/performance/reports/index.vue"),
  },
];
const disciplinaryRoutes = [
  {
    path: "/disciplinary/offence-types",
    name: "disciplinary.offence-types",
    meta: { title: setTitle("Offence Types"), authRequired: true, permission: "disciplinary-config.view" },
    component: () => import("@/views/disciplinary/offence-types/index.vue"),
  },
  {
    path: "/disciplinary/severity-levels",
    name: "disciplinary.severity-levels",
    meta: { title: setTitle("Severity Levels"), authRequired: true, permission: "disciplinary-config.view" },
    component: () => import("@/views/disciplinary/severity-levels/index.vue"),
  },
  {
    path: "/disciplinary/cases",
    name: "disciplinary.cases",
    meta: { title: setTitle("Disciplinary Cases"), authRequired: true, permission: "disciplinary.view-all|disciplinary.view-unit|disciplinary.raise|disciplinary.investigate|disciplinary.hear|disciplinary.manage" },
    component: () => import("@/views/disciplinary/cases/index.vue"),
  },
  {
    path: "/disciplinary/cases/new",
    name: "disciplinary.cases.new",
    meta: { title: setTitle("Raise a Case"), authRequired: true, permission: "disciplinary.raise|disciplinary.manage" },
    component: () => import("@/views/disciplinary/cases/raise.vue"),
  },
  {
    path: "/disciplinary/cases/:id",
    name: "disciplinary.case",
    meta: { title: setTitle("Disciplinary Case"), authRequired: true, permission: "disciplinary.view-all|disciplinary.view-unit|disciplinary.raise|disciplinary.investigate|disciplinary.hear|disciplinary.manage" },
    component: () => import("@/views/disciplinary/cases/detail.vue"),
  },
  {
    path: "/disciplinary/hearings",
    name: "disciplinary.hearings",
    meta: { title: setTitle("Hearings"), authRequired: true, permission: "disciplinary.view-all|disciplinary.view-unit|disciplinary.raise|disciplinary.investigate|disciplinary.hear|disciplinary.manage" },
    component: () => import("@/views/disciplinary/hearings/index.vue"),
  },
  {
    path: "/disciplinary/outcomes",
    name: "disciplinary.outcomes",
    meta: { title: setTitle("Outcomes"), authRequired: true, permission: "disciplinary.view-all|disciplinary.view-unit|disciplinary.raise|disciplinary.investigate|disciplinary.hear|disciplinary.manage" },
    component: () => import("@/views/disciplinary/outcomes/index.vue"),
  },
  {
    path: "/disciplinary/appeals",
    name: "disciplinary.appeals",
    meta: { title: setTitle("Appeals"), authRequired: true, permission: "disciplinary.view-all|disciplinary.view-unit|disciplinary.raise|disciplinary.investigate|disciplinary.hear|disciplinary.manage" },
    component: () => import("@/views/disciplinary/appeals/index.vue"),
  },
  {
    path: "/disciplinary/sanctions",
    name: "disciplinary.sanctions",
    meta: { title: setTitle("Sanctions"), authRequired: true, permission: "disciplinary-config.view" },
    component: () => import("@/views/disciplinary/sanctions/index.vue"),
  },
  {
    path: "/disciplinary/calendar",
    name: "disciplinary.calendar",
    meta: { title: setTitle("Disciplinary Calendar"), authRequired: true, permission: "disciplinary.view-all|disciplinary.view-unit|disciplinary.raise|disciplinary.investigate|disciplinary.hear|disciplinary.manage" },
    component: () => import("@/views/disciplinary/calendar/index.vue"),
  },
];
const trainingRoutes = [
  {
    path: "/training/categories",
    name: "training.categories",
    meta: { title: setTitle("Training Categories"), authRequired: true, permission: "training-config.view|training-config.manage" },
    component: () => import("@/views/training/categories/index.vue"),
  },
  {
    path: "/training/programs",
    name: "training.programs",
    meta: { title: setTitle("Training Programmes"), authRequired: true, permission: "training-config.view|training-config.manage" },
    component: () => import("@/views/training/programs/index.vue"),
  },
  {
    path: "/training/programs/:id",
    name: "training.program",
    meta: { title: setTitle("Training Programme"), authRequired: true, permission: "training-config.view|training-config.manage" },
    component: () => import("@/views/training/programs/editor.vue"),
  },
  {
    path: "/training/needs",
    name: "training.needs",
    meta: { title: setTitle("Training Needs"), authRequired: true, permission: "training-needs.view-all|training-needs.manage|training-needs.nominate" },
    component: () => import("@/views/training/needs/index.vue"),
  },
  {
    // An employee's own training. No permission: only their own is returned.
    path: "/my/training",
    name: "my.training",
    meta: { title: setTitle("My Training"), authRequired: true },
    component: () => import("@/views/training/my-training/index.vue"),
  },
  {
    path: "/training/sessions",
    name: "training.sessions",
    meta: { title: setTitle("Training Sessions"), authRequired: true, permission: "training-sessions.view|training-sessions.manage" },
    component: () => import("@/views/training/sessions/index.vue"),
  },
  {
    // The register: the session's trainer takes it whatever their role, so the
    // check is made on the session by the API rather than by a permission here.
    path: "/training/sessions/:id/register",
    name: "training.register",
    meta: { title: setTitle("Register"), authRequired: true },
    component: () => import("@/views/training/sessions/register.vue"),
  },
  {
    path: "/training/sessions/:id",
    name: "training.session",
    meta: { title: setTitle("Training Session"), authRequired: true, permission: "training-sessions.view|training-sessions.manage" },
    component: () => import("@/views/training/sessions/editor.vue"),
  },
  {
    path: "/training/budget",
    name: "training.budget",
    meta: { title: setTitle("Training Budget"), authRequired: true, permission: "training-budget.view|training-budget.manage" },
    component: () => import("@/views/training/budget/index.vue"),
  },
  {
    path: "/training/calendar",
    name: "training.calendar",
    meta: { title: setTitle("Training Calendar"), authRequired: true, permission: "training-sessions.view|training-sessions.manage" },
    component: () => import("@/views/training/calendar/index.vue"),
  },
  {
    path: "/training/enrollments",
    name: "training.enrollments",
    meta: { title: setTitle("Enrolments"), authRequired: true, permission: "training-sessions.manage|training-needs.manage|training-needs.nominate" },
    component: () => import("@/views/training/enrollments/index.vue"),
  },
  {
    path: "/training/attendance",
    name: "training.attendance",
    meta: { title: setTitle("Attendance"), authRequired: true, permission: "training-sessions.view|training-sessions.manage" },
    component: () => import("@/views/training/attendance/index.vue"),
  },
  {
    path: "/training/certifications",
    name: "training.certifications",
    meta: { title: setTitle("Certifications"), authRequired: true, permission: "training-needs.view-all|training-needs.manage|training-needs.nominate" },
    component: () => import("@/views/training/certifications/index.vue"),
  },
  {
    path: "/training/reports",
    name: "training.reports",
    meta: { title: setTitle("Training Reports"), authRequired: true, permission: "training-reports.view" },
    component: () => import("@/views/training/reports/index.vue"),
  },
];
const hrReportsRoutes = [
  {
    path: "/hr-reports/headcount",
    name: "hr-reports.headcount",
    meta: { title: setTitle("Headcount Report"), authRequired: true, permission: "reports.view" },
    component: () => import("@/views/hr-reports/headcount/index.vue"),
  },
  {
    path: "/hr-reports/turnover",
    name: "hr-reports.turnover",
    meta: { title: setTitle("Turnover Report"), authRequired: true, permission: "reports.view" },
    component: () => import("@/views/hr-reports/turnover/index.vue"),
  },
  {
    path: "/hr-reports/payroll-summary",
    name: "hr-reports.payroll-summary",
    meta: { title: setTitle("Payroll Summary"), authRequired: true, permission: "payroll.view" },
    component: () => import("@/views/hr-reports/payroll-summary/index.vue"),
  },
  {
    path: "/hr-reports/leave-summary",
    name: "hr-reports.leave-summary",
    meta: { title: setTitle("Leave Summary"), authRequired: true, permission: "reports.view" },
    component: () => import("@/views/hr-reports/leave-summary/index.vue"),
  },
  // Training and performance already have full reports in their own modules;
  // these addresses lead there rather than keeping a second copy.
  { path: "/hr-reports/training-summary", name: "hr-reports.training-summary", redirect: { name: "training.reports" } },
  { path: "/hr-reports/performance-summary", name: "hr-reports.performance-summary", redirect: { name: "performance.reports" } },
  {
    path: "/hr-reports/compliance",
    name: "hr-reports.compliance",
    meta: { title: setTitle("Compliance Report"), authRequired: true, permission: "reports.view" },
    component: () => import("@/views/hr-reports/compliance/index.vue"),
  },
];

const assetRoutes = [
  {
    // "/assets-management", not "/assets": the built frontend serves its
    // JS and CSS out of an "assets" directory, so a bare /assets URL makes
    // nginx try to serve that directory instead of falling through to the
    // SPA — which 404s on a page refresh.
    path: "/assets-management",
    name: "assets.dashboard",
    meta: { title: setTitle("Assets"), authRequired: true, permission: "assets.view" },
    component: () => import("@/views/assets/dashboard/index.vue"),
  },
  {
    path: "/assets-management/audits",
    name: "assets.audits",
    meta: { title: setTitle("Asset Counts"), authRequired: true, permission: "assets.view" },
    component: () => import("@/views/assets/audits/index.vue"),
  },
  {
    path: "/assets-management/setup",
    name: "assets.setup",
    meta: { title: setTitle("Asset Setup"), authRequired: true, permission: "assets.view" },
    component: () => import("@/views/assets/setup/index.vue"),
  },
  {
    path: "/assets-management/categories",
    name: "assets.categories",
    meta: { title: setTitle("Asset Categories"), authRequired: true, permission: "assets.view" },
    component: () => import("@/views/assets/categories/index.vue"),
  },
  {
    path: "/assets-management/register",
    name: "assets.register",
    meta: { title: setTitle("Asset Register"), authRequired: true, permission: "assets.view" },
    component: () => import("@/views/assets/register/index.vue"),
  },
  {
    path: "/assets-management/assignments",
    name: "assets.assignments",
    meta: { title: setTitle("Asset Assignments"), authRequired: true, permission: "assets.view" },
    component: () => import("@/views/assets/assignments/index.vue"),
  },
  {
    path: "/assets-management/returns",
    name: "assets.returns",
    meta: { title: setTitle("Asset Returns"), authRequired: true, permission: "assets.view" },
    component: () => import("@/views/assets/returns/index.vue"),
  },
  {
    path: "/assets-management/transfers",
    name: "assets.transfers",
    meta: { title: setTitle("Asset Transfers"), authRequired: true, permission: "assets.view" },
    component: () => import("@/views/assets/transfers/index.vue"),
  },
  {
    path: "/assets-management/maintenance",
    name: "assets.maintenance",
    meta: { title: setTitle("Asset Maintenance"), authRequired: true, permission: "assets.view" },
    component: () => import("@/views/assets/maintenance/index.vue"),
  },
  {
    path: "/assets-management/requests",
    name: "assets.requests",
    meta: { title: setTitle("Asset Requests"), authRequired: true, permission: "assets.request" },
    component: () => import("@/views/assets/requests/index.vue"),
  },
  {
    path: "/assets-management/clearance",
    name: "assets.clearance",
    meta: { title: setTitle("Asset Clearance"), authRequired: true, permission: "assets.view" },
    component: () => import("@/views/assets/clearance/index.vue"),
  },
  {
    path: "/assets-management/reports/register",
    name: "assets.reports.register",
    meta: { title: setTitle("Asset Register Report"), authRequired: true, permission: "assets.view" },
    component: () => import("@/views/assets/reports/screen/index.vue"),
  },
  {
    path: "/assets-management/reports/employee",
    name: "assets.reports.employee",
    meta: { title: setTitle("Employee Asset Report"), authRequired: true, permission: "assets.view" },
    component: () => import("@/views/assets/reports/screen/index.vue"),
  },
  {
    path: "/assets-management/reports/unreturned",
    name: "assets.reports.unreturned",
    meta: { title: setTitle("Unreturned Assets"), authRequired: true, permission: "assets.view" },
    component: () => import("@/views/assets/reports/screen/index.vue"),
  },
  {
    path: "/assets-management/reports/warranty",
    name: "assets.reports.warranty",
    meta: { title: setTitle("Warranty Expiry"), authRequired: true, permission: "assets.view" },
    component: () => import("@/views/assets/reports/screen/index.vue"),
  },
  {
    path: "/assets-management/reports/maintenance",
    name: "assets.reports.maintenance",
    meta: { title: setTitle("Maintenance Report"), authRequired: true, permission: "assets.view" },
    component: () =>
      import("@/views/assets/reports/screen/index.vue"),
  },
  {
    path: "/assets-management/reports/lost-damaged",
    name: "assets.reports.lost-damaged",
    meta: { title: setTitle("Lost & Damaged Report"), authRequired: true, permission: "assets.view" },
    component: () => import("@/views/assets/reports/lost-damaged/index.vue"),
  },
];
// Help & Support: tickets raised with Demulla, followed in Demulla's CRM.
const helpCenterRoutes = [
  {
    path: "/help-center/tickets",
    name: "help-center.tickets",
    meta: { title: setTitle("Help & Support"), authRequired: true, permission: "help-center.raise|help-center.view-all" },
    component: () => import("@/views/help-center/tickets/index.vue"),
  },
  {
    path: "/help-center/raise",
    name: "help-center.raise",
    meta: { title: setTitle("Raise a ticket"), authRequired: true, permission: "help-center.raise" },
    component: () => import("@/views/help-center/raise/index.vue"),
  },
  {
    path: "/help-center/tickets/:ref",
    name: "help-center.ticket",
    meta: { title: setTitle("Support ticket"), authRequired: true, permission: "help-center.raise|help-center.view-all" },
    component: () => import("@/views/help-center/ticket/index.vue"),
  },
  {
    path: "/help-center/settings",
    name: "help-center.settings",
    meta: { title: setTitle("Help & Support connection"), authRequired: true, permission: "help-center.configure" },
    component: () => import("@/views/help-center/settings/index.vue"),
  },
];

export const allRoute = [
  ...authRoutes,
  ...errorRoutes,
  ...dashboardRoutes,
  ...organogramRoutes,
  ...documentRoutes,
  ...recruitmentRoutes,
  ...attendanceRoutes,
  ...leaveRoutes,
  ...systemAdministrationRoutes,
  ...payrollRoutes,
  ...assetRoutes,
  ...performanceRoutes,
  ...disciplinaryRoutes,
  ...trainingRoutes,
  ...hrReportsRoutes,
  ...onboardingRoutes,
  ...employeeScaffoldingRoutes,
  ...employeeRoutes,
  ...configRoutes,
  ...tenantLogRoutes,
  ...headcountRoutes,
  ...workforceRoutes,
  ...successionRoutes,
  ...approvalRoutes,
  ...exportRoutes,
  ...companyRoutes,
  ...administrationRoutes,
  ...helpCenterRoutes,
];
